import {
  FluidAlignment,
  FluidColor,
  FluidCustomToggleConfig,
  FluidFormElementState,
  FluidIconConfig,
  FluidIconKey,
  FluidPopoverConfig,
  FluidProgressState,
  FluidSize,
  FluidTextOverflow,
  FluidTheme,
  FluidToggleConfig,
  FluidToggleType,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { InfoPopover } from '../../../common/fluid-common-icon.components';
import {
  SWITCH_LABEL_EXCEEDS_MAX_LENGTH,
  SWITCH_LABEL_MAX_LENGTH,
} from '../../../constants/constants';
import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { EventUtils } from '../../../utils/events/event-utils';
import { getFormInputId } from '../../../utils/forms/form-method.utils';
import { Utils } from '../../../utils/utils';

export interface ToggleProps extends FluidFunctionalComponentProps {
  config?: FluidToggleConfig;
  controlName?: string;
  toggleType?: FluidToggleType;
  label?: string;
  labelTextOverflow?: FluidTextOverflow;
  labelAlignment?: FluidAlignment;
  disabled?: boolean;
  readonly?: boolean;
  helpText?: string | FluidPopoverConfig;
  inlineField?: boolean;
  theme?: FluidTheme;

  labelOn?: string;
  labelOff?: string;
  customToggleConfig?: FluidCustomToggleConfig;
  textToggleConfig?: FluidCustomToggleConfig & {
    fluidTranslate?: { textOn?: string; textOff?: string };
  };

  setRef?: (el: HTMLElement) => void;
  update?: (value: any) => void;
  markTouchedAndValidate?: () => void;

  _elementState?: FluidFormElementState;
  _i18nStore?: FluidI18nStore | undefined;
  _asMenuItem?: boolean;
  _removePadding?: boolean;
}

// ================================================================ //
// -- Toggle Functional Components

/**
 * Checkbox Input
 */
const CheckboxInput: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const {
    config,
    controlName,
    disabled,
    readonly,
    hidden,
    setRef,
    update,
    markTouchedAndValidate,
    _elementState,
  } = props;

  return (
    <input
      type='checkbox'
      id={getFormInputId(config?.id, controlName)}
      name={_elementState.name}
      value={_elementState.value}
      checked={_elementState.value}
      indeterminate={_elementState.value === FluidProgressState.INDETERMINATE}
      disabled={disabled}
      ref={(el) => setRef(el)}
      onClick={readonly && ((event) => event.preventDefault())}
      onBlur={() => markTouchedAndValidate()}
      onChange={() => update({ target: { value: !props._elementState.value } })}
      class={{
        'toggle-input': true,
        'input-hidden': hidden,
      }}
    />
  );
};

/**
 * Checkbox Label
 */
const CheckboxLabel: FunctionalComponent<ToggleProps> = (
  props: ToggleProps,
  children
) => {
  const {
    label,
    labelAlignment,
    labelTextOverflow,
    inlineField,
    helpText,
    disabled,
    readonly,
    theme,
    _asMenuItem,
    _removePadding,
  } = props;

  let popoverRef: HTMLFluidPopoverElement;

  // Prevents Popover click from changing Toggle's "selected" state
  const cancelPopoverClick = (event: MouseEvent) => {
    if (!popoverRef || !EventUtils.eventPathIncludes(event, popoverRef)) return;
    event.preventDefault();
  };

  return (
    <label
      onClick={!!helpText && ((event) => cancelPopoverClick(event))}
      onMouseDown={(event) => event.preventDefault()} // Prevent <label> from stealing focus
      class={{
        'toggle-label': true,
        'block-layout': !inlineField,
        'menu-item': _asMenuItem,
        'no-padding': _removePadding,
        'read-only': readonly,
        disabled: disabled,
        [`${props.class}`]: !!props.class,
      }}>
      {children}
      {!!label && (
        <span
          title={labelTextOverflow !== FluidTextOverflow.WRAP ? label : null}
          class={{
            'toggle-label-text': true,
            [`text-overflow-${labelTextOverflow}`]: true,
            'align-left': labelAlignment === FluidAlignment.LEFT,
            'align-right': labelAlignment === FluidAlignment.RIGHT,
          }}>
          {label}
          <InfoPopover
            ref={(el) => (popoverRef = el as HTMLFluidPopoverElement)}
            helpText={helpText}
            iconColor={!disabled ? FluidColor.BLUE : null}
            theme={theme}
          />
        </span>
      )}
    </label>
  );
};

/**
 * Checkbox with Label
 */
export const Checkbox: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  return (
    <CheckboxLabel {...props}>
      <div class='toggle-container'>
        <CheckboxInput {...props} />
      </div>
    </CheckboxLabel>
  );
};

/**
 * Toggle Switch: Icons
 */
export const SwitchIcons: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { checked, theme } = props;

  return [
    <span class='toggle-switch-on'>
      <fluid-icon
        aria-hidden={String(!checked)}
        config={{
          theme: theme,
          overrideGlobalTheme: !!theme,
          ...props.iconOn,
          color: FluidColor.WHITE,
          size: FluidSize.SMALL,
        }}
      />
    </span>,
    <span class='toggle-switch-off'>
      <fluid-icon
        aria-hidden={String(checked)}
        config={{
          theme: theme,
          overrideGlobalTheme: !!theme,
          ...props.iconOff,
          color: FluidColor.GRAY,
          size: FluidSize.SMALL,
        }}
      />
    </span>,
  ];
};

/**
 * Toggle Switch: Text
 */
export const SwitchText: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { labelOn, labelOff, checked } = props;
  const validateLabel = (label: string) => {
    if (label?.length > SWITCH_LABEL_MAX_LENGTH) {
      Utils.consoleWarn(
        { tagName: 'fluid-toggle' } as HTMLElement,
        SWITCH_LABEL_EXCEEDS_MAX_LENGTH
      );
      return '';
    }
    return label;
  };

  return [
    <span class='toggle-switch-on' aria-hidden={String(!checked)}>
      {validateLabel(labelOn)}
    </span>,
    <span class='toggle-switch-off' aria-hidden={String(checked)}>
      {validateLabel(labelOff)}
    </span>,
  ];
};

/**
 * Toggle Switch
 */
export const Switch: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const {
    disabled,
    customToggleConfig,
    labelOn,
    labelOff,
    theme,
    _elementState,
  } = props;
  const hasLabel = !!labelOn || !!labelOff;
  const checked = (_elementState as FluidFormElementState).value;

  return (
    <CheckboxLabel {...props} class='toggle-switch'>
      <div
        class={{
          'toggle-switch-container': true,
          'toggle-checked': checked,
          'switch-disabled': disabled,
        }}>
        <CheckboxInput {...props} hidden={true} />
        {!!customToggleConfig && (
          <SwitchIcons
            iconOn={customToggleConfig.iconOn}
            iconOff={customToggleConfig.iconOff}
            checked={checked}
            theme={theme}
          />
        )}
        {!customToggleConfig && hasLabel && (
          <SwitchText
            labelOn={labelOn}
            labelOff={labelOff}
            checked={checked}
            theme={theme}
          />
        )}
      </div>
    </CheckboxLabel>
  );
};

/**
 * Link Toggle
 */
export const LinkToggle: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { size, theme, customToggleConfig } = props;

  const iconConfig: FluidIconConfig = {
    size: size || FluidSize.SMALL,
    theme: theme,
    overrideGlobalTheme: !!theme,
  };

  const iconOn: FluidIconConfig = {
    ...iconConfig,
    key: FluidIconKey.LINK,
    color: FluidColor.YELLOW,
    ...customToggleConfig?.iconOn,
  };

  const iconOff: FluidIconConfig = {
    ...iconConfig,
    key: FluidIconKey.UNLINK,
    color: FluidColor.BLUE,
    ...customToggleConfig?.iconOff,
  };

  return (
    <CustomToggle
      {...props}
      customToggleConfig={{ iconOn, iconOff }}
      class='link-toggle'
    />
  );
};

/**
 * Favorite Toggle
 */
export const FavoriteToggle: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { size, theme, customToggleConfig } = props;

  const iconConfig: FluidIconConfig = {
    size: size || FluidSize.MEDIUM,
    theme: theme,
    overrideGlobalTheme: !!theme,
  };

  const iconOn: FluidIconConfig = {
    ...iconConfig,
    key: FluidIconKey.STAR_FILLED,
    color: FluidColor.YELLOW,
    ...customToggleConfig?.iconOn,
  };

  const iconOff: FluidIconConfig = {
    ...iconConfig,
    key: FluidIconKey.STAR_OUTLINE,
    color: undefined,
    ...customToggleConfig?.iconOff,
  };
  return (
    <CustomToggle
      {...props}
      customToggleConfig={{ iconOn, iconOff }}
      class='favorite-toggle'
    />
  );
};

/**
 * Flag Toggle
 */
export const FlagToggle: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { size, theme, customToggleConfig } = props;

  const iconConfig: FluidIconConfig = {
    key: FluidIconKey.FLAG,
    size: size || FluidSize.SMALL,
    theme: theme,
    overrideGlobalTheme: !!theme,
  };

  const iconOn: FluidIconConfig = {
    ...iconConfig,
    color: FluidColor.ERROR,
    ...customToggleConfig?.iconOn,
  };

  const iconOff: FluidIconConfig = {
    ...iconConfig,
    color: undefined, // Explicitly remove color so it can be set via CSS
    ...customToggleConfig?.iconOff,
  };

  return (
    <CustomToggle
      {...props}
      customToggleConfig={{ iconOn, iconOff }}
      class='flag-toggle'
    />
  );
};

/**
 * Custom Toggle
 */
export const CustomToggle: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { customToggleConfig, disabled, theme, _elementState } = props;
  const { iconOn, iconOff } = customToggleConfig;

  return (
    <CheckboxLabel
      {...props}
      class={{
        'custom-toggle': true,
        [`${props.class}`]: !!props.class,
      }}>
      <div class='toggle-container'>
        <CheckboxInput {...props} hidden={true} />
        <fluid-icon
          config={{
            theme: theme,
            overrideGlobalTheme: !!theme,
            size: FluidSize.SMALL,
            ...((_elementState as FluidFormElementState).value
              ? iconOn
              : iconOff),
            disabled,
          }}
        />
      </div>
    </CheckboxLabel>
  );
};

/**
 * Text Toggle
 */
export const TextToggle: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { disabled, theme, _elementState, _i18nStore } = props;
  const { textOn, textOff, fluidTranslate } = props.textToggleConfig;

  return (
    <CheckboxLabel {...props} class='custom-toggle'>
      <div class='toggle-container'>
        <CheckboxInput {...props} hidden={true} />
        <fluid-link
          tabIndex={-1}
          disabled={disabled}
          theme={theme}
          overrideGlobalTheme={true}>
          {(_elementState as FluidFormElementState).value
            ? _i18nStore?.translate(fluidTranslate?.textOn) || textOn
            : _i18nStore?.translate(fluidTranslate?.textOff) || textOff}
        </fluid-link>
      </div>
    </CheckboxLabel>
  );
};

/**
 * Chip Toggle (with/without checkbox)
 */
export const ChipToggle: FunctionalComponent<ToggleProps> = (
  props: ToggleProps
) => {
  const { toggleType, _elementState } = props;
  const hideCheckbox = toggleType === FluidToggleType.CHIP;

  return (
    <CheckboxLabel
      {...props}
      helpText={null}
      labelTextOverflow={FluidTextOverflow.ELLIPSIS}
      inlineField={true}
      class={{
        'chip-toggle': true,
        'toggle-checked': !!_elementState.value,
      }}>
      <div
        class={{
          'toggle-container': true,
          'sr-only': hideCheckbox,
        }}>
        <CheckboxInput {...props} hidden={hideCheckbox} />
      </div>
    </CheckboxLabel>
  );
};
